import React, { useState, useEffect, useMemo } from 'react';
import Helmet from '../components/Helmet/Helmet';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/login.css';
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup } from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { storage, db, auth, googleProvider } from '../firebase.config';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { getAuthErrorMessage } from '../services/authErrors';
import useAuth from '../custom-hooks/useAuth';
import bannerImg from '../assets/images/bedroom.jpg';
import { useDispatch } from 'react-redux';
import { cartActions } from '../redux/slices/cartSlice';
import { wishlistActions } from '../redux/slices/wishlistSlice';
import { fetchUserData } from '../services/userSyncService';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

const Signup = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // If user is already logged in, redirect to home (only when not actively registering)
  useEffect(() => {
    if (!authLoading && currentUser && !loading && !googleLoading) {
      toast.info('You are already logged in! Redirecting...');
      navigate('/');
    }
  }, [currentUser, authLoading, loading, googleLoading, navigate]);

  // Real-time validations
  const passwordCriteria = useMemo(() => {
    return {
      length: password.length >= 6,
      hasLetter: /[a-zA-Z]/.test(password),
      hasNumber: /\d/.test(password),
    };
  }, [password]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        toast.error('Please select a valid image file (PNG, JPG, JPEG)');
        return;
      }
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  const removeSelectedFile = () => {
    setFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    setGoogleLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Ensure user document exists in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userDocRef);

      if (!userSnap.exists()) {
        await setDoc(userDocRef, {
          uid: user.uid,
          displayName: user.displayName || user.email.split('@')[0],
          email: user.email,
          photoURL: user.photoURL || null,
          createdAt: new Date().toISOString(),
          role: 'customer',
        });
        toast.success(`Account created successfully! Welcome ${user.displayName || ''}!`);
      } else {
        if (user.photoURL && (!userSnap.data()?.photoURL || userSnap.data()?.photoURL !== user.photoURL)) {
          await setDoc(userDocRef, { photoURL: user.photoURL }, { merge: true });
        }
        toast.info(`You already have an account! Welcome back, ${user.displayName || ''}!`);
      }

      // Cache profile locally and hydrate remote store
      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0],
        photoURL: user.photoURL || userSnap.data()?.photoURL || null,
        role: 'customer',
      };
      localStorage.setItem('cachedAuthProfile', JSON.stringify(profileData));

      const { cart, wishlist } = await fetchUserData(user.uid);
      dispatch(cartActions.setCart(cart));
      dispatch(wishlistActions.setWishlist(wishlist));

      navigate('/');
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        setGoogleLoading(false);
        return;
      }
      const friendlyError = getAuthErrorMessage(error);
      setErrorMessage(friendlyError);
      toast.error(friendlyError);
    } finally {
      setGoogleLoading(false);
    }
  };

  const signup = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Pre-flight Validations
    if (username.trim().length < 3) {
      setErrorMessage('Username must be at least 3 characters long.');
      toast.error('Username must be at least 3 characters long.');
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      toast.error('Please enter a valid email address.');
      return;
    }

    if (!passwordCriteria.length) {
      setErrorMessage('Password must be at least 6 characters long.');
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      // 1. Create User in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;

      let photoDownloadUrl = '';

      // 2. Upload Profile Image IF provided (Optional)
      if (file) {
        try {
          const storageRef = ref(storage, `user_avatars/${user.uid}_${Date.now()}`);
          const uploadTask = uploadBytesResumable(storageRef, file);
          const snapshot = await uploadTask;
          photoDownloadUrl = await getDownloadURL(snapshot.ref);
        } catch (storageErr) {
          console.warn('Avatar upload failed, continuing with default avatar:', storageErr);
        }
      }

      // 3. Update Auth Profile
      await updateProfile(user, {
        displayName: username.trim(),
        photoURL: photoDownloadUrl || null,
      });

      // 4. Save User Document in Firestore
      const profileData = {
        uid: user.uid,
        displayName: username.trim(),
        email: email.trim(),
        photoURL: photoDownloadUrl || null,
        createdAt: new Date().toISOString(),
        role: 'customer',
      };
      await setDoc(doc(db, 'users', user.uid), profileData);

      // Cache profile locally so the header reflects the new user immediately
      localStorage.setItem('cachedAuthProfile', JSON.stringify(profileData));

      setLoading(false);
      toast.success('Account created successfully! Welcome to L\'HABILLEUR');
      navigate('/');
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/email-already-in-use') {
        const msg = 'This email is already registered! Redirecting you to login...';
        setErrorMessage(msg);
        toast.info(msg);
        setTimeout(() => {
          navigate('/login', { state: { email: email.trim(), alreadyRegistered: true } });
        }, 1500);
        return;
      }
      const friendlyError = getAuthErrorMessage(error);
      setErrorMessage(friendlyError);
      toast.error(friendlyError);
    }
  };

  return (
    <Helmet title="Sign Up | L'HABILLEUR">
      <section className="auth__section">
        <div className="auth__container-wide">
          <motion.div 
            className="auth__card-split signup-card"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Side Showcase Banner */}
            <div 
              className="auth__banner-side"
              style={{ backgroundImage: `url(${bannerImg})` }}
            >
              <div className="auth__banner-overlay"></div>
              <div className="auth__banner-content">
                <div className="auth__banner-brand">
                  <h2>L'HABILLEUR</h2>
                  <p>Shop the finest curated furniture, modern tech, and designer timepieces in Egypt.</p>
                </div>

                <div className="auth__banner-features">
                  <div className="auth__feature-item">
                    <i className="ri-truck-line"></i>
                    <span>Free & Fast Shipping across Egypt</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-shield-check-line"></i>
                    <span>100% Genuine Certified Brands</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-arrow-go-back-line"></i>
                    <span>14-Day Easy Return Guarantee</span>
                  </div>
                </div>

                <div className="auth__banner-footer">
                  <i className="ri-checkbox-circle-fill"></i>
                  <span>Trusted by over 10,000+ satisfied shoppers</span>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div className="auth__form-side">
              <div className="auth__header-compact">
                <h2>Create an Account</h2>
                <p>Sign up in seconds to start shopping</p>
              </div>

              {errorMessage && (
                <div className="auth__alert">
                  <i className="ri-error-warning-fill"></i>
                  <div>{errorMessage}</div>
                </div>
              )}

              {/* Google Sign-up Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="auth__google-btn"
              >
                {googleLoading ? (
                  <>
                    <i className="ri-loader-4-line ri-spin"></i> Connecting to Google...
                  </>
                ) : (
                  <>
                    <GoogleIcon /> Sign up with Google
                  </>
                )}
              </button>

              <div className="auth__divider">
                <span>or register with email</span>
              </div>

              <form onSubmit={signup} noValidate>
                {/* Row 1: Username & Email side-by-side */}
                <div className="auth__form-grid-2">
                  <div className="auth__form-group">
                    <label htmlFor="username">Full Name</label>
                    <div className="auth__input-wrapper">
                      <i className="ri-user-line auth__input-icon"></i>
                      <input
                        id="username"
                        type="text"
                        className="auth__input"
                        placeholder="e.g. Mohamed Saleh"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="auth__form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="auth__input-wrapper">
                      <i className="ri-mail-line auth__input-icon"></i>
                      <input
                        id="email"
                        type="email"
                        className="auth__input"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Row 2: Password & Confirm Password side-by-side */}
                <div className="auth__form-grid-2">
                  <div className="auth__form-group">
                    <label htmlFor="password">Password</label>
                    <div className="auth__input-wrapper">
                      <i className="ri-lock-line auth__input-icon"></i>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        className="auth__input"
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="auth__toggle-password"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="Toggle password visibility"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>

                  <div className="auth__form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="auth__input-wrapper">
                      <i className="ri-shield-keyhole-line auth__input-icon"></i>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="auth__input"
                        placeholder="Re-enter password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="auth__toggle-password"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label="Toggle confirm password visibility"
                      >
                        <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                    {confirmPassword && (
                      <small style={{ color: passwordsMatch ? '#16a34a' : '#dc2626', fontSize: '0.74rem', marginTop: '2px', display: 'block' }}>
                        {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </small>
                    )}
                  </div>
                </div>

                {/* Row 3: Password Guide & Optional Avatar side-by-side */}
                <div className="auth__form-grid-2">
                  <div className="password__rules-compact">
                    <span className="password__rules-title">Requirements:</span>
                    <div className={`password__rule-item ${passwordCriteria.length ? 'valid' : ''}`}>
                      <i className={passwordCriteria.length ? 'ri-checkbox-circle-fill' : 'ri-circle-line'}></i>
                      6+ characters
                    </div>
                    <div className={`password__rule-item ${passwordCriteria.hasLetter ? 'valid' : ''}`}>
                      <i className={passwordCriteria.hasLetter ? 'ri-checkbox-circle-fill' : 'ri-circle-line'}></i>
                      Contains letters & numbers
                    </div>
                  </div>

                  {/* Optional Avatar */}
                  <div className="avatar__upload-compact">
                    <div className="avatar__preview-container">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" className="avatar__preview-img" />
                      ) : (
                        <i className="ri-user-smile-line avatar__preview-placeholder"></i>
                      )}
                    </div>
                    <div className="avatar__upload-info">
                      <div className="avatar__upload-title">
                        Photo <span className="avatar__upload-badge">Optional</span>
                      </div>
                      <div className="avatar__actions">
                        <label htmlFor="avatar-file" className="avatar__choose-btn">
                          <i className="ri-upload-2-line"></i> {file ? 'Change' : 'Upload'}
                        </label>
                        <input
                          id="avatar-file"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                        />
                        {file && (
                          <button type="button" onClick={removeSelectedFile} className="avatar__remove-btn">
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button type="submit" className="auth__submit-btn" disabled={loading || googleLoading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i> Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>

                <div className="auth__footer-text">
                  Already have an account? <Link to="/login">Sign In</Link>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </Helmet>
  );
};

export default Signup;